import { useState, useEffect } from "react";
import CrudTable from "../components/CrudTable";
import PDFButton from "../components/PDFButton";
import { api, BACKEND_URL } from "../lib/api";
import { FileCCE, Modal } from "../components/ui";
import { Upload, User, Paperclip } from "lucide-react";

const TABS = [
  { key: "brigades", label: "Brigadistas" },
  { key: "emergency_equipment", label: "Equipos de Emergencia" },
  { key: "msds", label: "HDS" },
  { key: "brigade_trainings", label: "Capacitaciones" },
  { key: "drills", label: "Simulacros" },
  { key: "emergency_reports", label: "Reportes de Emergencia" },
  { key: "spills", label: "Derrames" },
  { key: "brigade_meetings", label: "Reuniones" },
  { key: "emergency_procedures", label: "Procedimientos de Atención" },
];

const CRETIB = [
  { key: "cretib_c", label: "C — Corrosivo", color: "#e67e22" },
  { key: "cretib_r", label: "R — Reactivo", color: "#9b59b6" },
  { key: "cretib_e", label: "E — Explosivo", color: "#8e2424" },
  { key: "cretib_t", label: "T — Tóxico", color: "#e74c3c" },
  { key: "cretib_i", label: "I — Inflamable", color: "#f1c40f" },
  { key: "cretib_b", label: "B — Bio-infeccioso", color: "#1d6e4a" },
];

function cretibBadges(row) {
  return (
    <div className="flex flex-wrap gap-1">
      {CRETIB.filter((c) => row?.[c.key]).map((c) => (
        <span key={c.key} className="badge" style={{ background: c.color, color: "#fff", padding: "2px 6px", borderRadius: 4, fontSize: 11 }} title={c.label}>{c.key.slice(-1).toUpperCase()}</span>
      ))}
    </div>
  );
}

function hdsAgeBadge(row) {
  if (!row?.last_update_hds) return <span className="text-dim text-xs">—</span>;
  const years = (Date.now() - new Date(row.last_update_hds).getTime()) / (1000 * 60 * 60 * 24 * 365);
  if (years > 5) return <span className="badge badge-red">{years.toFixed(1)} años</span>;
  if (years > 3) return <span className="badge badge-yellow">{years.toFixed(1)} años</span>;
  return <span className="badge badge-green">{years.toFixed(1)} años</span>;
}

function nextInspectionBadge(row) {
  if (!row?.next_inspection) return <span className="text-dim text-xs">—</span>;
  const days = Math.round((new Date(row.next_inspection).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return <span className="badge badge-red">{Math.abs(days)} d vencido</span>;
  if (days < 30) return <span className="badge badge-yellow">{days} d</span>;
  return <span className="badge badge-green">{days} d</span>;
}

const fieldsByColl = {
  brigades: [
    { key: "photo_url", label: "Foto (URL/relativa)", hidden: true, hiddenInForm: true },
    { key: "name", label: "Nombre", required: true,
      render: (_v, row) => <BrigadistaCell row={row} /> },
    { key: "file_id", label: "Ficha/ID" },
    { key: "department", label: "Departamento" },
    { key: "phone", label: "Teléfono" },
    { key: "nss", label: "NSS" },
    { key: "allergies", label: "Alergias" },
    { key: "leader", label: "Líder inmediato" },
    { key: "emergency_phone", label: "Tel. emergencia" },
    { key: "status", label: "Status", type: "select", options: ["Activo", "Inactivo"] },
  ],
  emergency_equipment: [
    { key: "folio", label: "Folio/ID", required: true },
    { key: "type", label: "Tipo", type: "select", options: ["Kit contraderrames", "Trajes insectos", "Trajes derrames", "Equipo emergencia", "Otro"] },
    { key: "type_other", label: "Especificar (si tipo = Otro)" },
    { key: "description_extra", label: "Descripción adicional", type: "textarea", full: true },
    { key: "location", label: "Ubicación", required: true },
    { key: "last_inspection", label: "Última inspección", type: "date" },
    { key: "next_inspection", label: "Próxima inspección", type: "date",
      render: (_v, row) => nextInspectionBadge(row) },
    { key: "inspector", label: "Inspeccionado por" },
    { key: "condition", label: "Estado", type: "select", options: ["Operativo", "Requiere mantenimiento", "Fuera de servicio"] },
    { key: "replacement_cost", label: "Costo reposición ($MXP)", type: "number" },
  ],
  msds: [
    { key: "substance", label: "Sustancia", required: true },
    { key: "location", label: "Ubicación" },
    { key: "quantity", label: "Cantidad" },
    { key: "responsible_area", label: "Responsable del área" },
    { key: "last_update_hds", label: "Última actualización HDS", type: "date",
      render: (_v, row) => hdsAgeBadge(row) },
    { key: "provider_text", label: "Proveedor (texto)" },
    { key: "provider_id", label: "Proveedor (catálogo)", type: "select", options: [], full: false },
    { key: "first_aid", label: "Primeros auxilios", type: "textarea", full: true },
    { key: "risks", label: "Riesgos", type: "textarea", full: true },
    { key: "ppe", label: "EPP requerido" },
    { key: "manufacturer", label: "Fabricante" },
    ...CRETIB.map((c) => ({ key: c.key, label: c.label, type: "checkbox" })),
    { key: "cretib_badges", label: "CRETIB", hiddenInForm: true,
      render: (_v, row) => cretibBadges(row) },
  ],
  brigade_trainings: [
    { key: "topic", label: "Tema", required: true },
    { key: "date", label: "Fecha", type: "date" },
    { key: "presenter", label: "Presentador" },
    { key: "status", label: "Status", type: "select", options: ["Programada", "Completada", "Cancelada"] },
  ],
  drills: [
    { key: "code", label: "# Simulacro" },
    { key: "type", label: "Tipo" },
    { key: "description", label: "Descripción", type: "textarea", full: true },
    { key: "scheduled_date", label: "Fecha programada", type: "date" },
    { key: "execution_date", label: "Fecha ejecución", type: "date" },
    { key: "participants", label: "Participantes" },
    { key: "status", label: "Status", type: "select", options: ["Programado", "Ejecutado", "Cancelado"] },
  ],
  emergency_reports: [
    { key: "code", label: "# Reporte" },
    { key: "type", label: "Tipo", type: "select", options: ["Derrame", "Avistamiento especie", "Riesgo fauna", "Condición insegura", "Otro"] },
    { key: "area", label: "Área" },
    { key: "reported_by", label: "Reporta" },
    { key: "attended_by", label: "Atiende" },
    { key: "date", label: "Fecha", type: "date" },
    { key: "notification_time", label: "Hora aviso (HH:MM)" },
    { key: "notification_channel", label: "Medio de aviso", type: "select", options: ["Radio", "Teléfono", "Correo", "Presencial", "Alarma", "Otro"] },
    { key: "status", label: "Status", type: "select", options: ["Abierto", "En atención", "Cerrado"] },
    { key: "comments", label: "Comentarios", type: "textarea", full: true },
    { key: "improvements", label: "Mejoras propuestas", type: "textarea", full: true },
    { key: "lesson_learned", label: "Lección aprendida", type: "textarea", full: true },
  ],
  emergency_procedures: [
    { key: "code", label: "Código / Folio" },
    { key: "title", label: "Título del procedimiento", required: true, full: true },
    { key: "version", label: "Versión" },
    { key: "issue_date", label: "Fecha emisión", type: "date" },
    { key: "next_review", label: "Próxima revisión", type: "date" },
    { key: "scope", label: "Alcance", type: "textarea", full: true },
    { key: "responsible", label: "Responsable" },
    { key: "approver", label: "Aprobado por" },
    { key: "status", label: "Status", type: "select", options: ["Borrador","Vigente","En revisión","Obsoleto"] },
    { key: "comments", label: "Comentarios", type: "textarea", full: true },
  ],
  brigade_meetings: [
    { key: "date", label: "Fecha", type: "date" },
    { key: "topics", label: "Temas", type: "textarea", full: true },
    { key: "attendees", label: "Asistentes" },
    { key: "agreements", label: "Acuerdos", type: "textarea", full: true },
  ],
};

function BrigadistaCell({ row }) {
  const initials = (row?.name || "??").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!row?.photo_url) { setBlobUrl(null); return; }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        // If it's an absolute URL we use it directly; otherwise fetch with auth
        if (row.photo_url.startsWith("http")) { setBlobUrl(row.photo_url); return; }
        const token = localStorage.getItem("token");
        const audit = localStorage.getItem("audit_tenant_id");
        const headers = { Authorization: `Bearer ${token}` };
        if (audit) headers["X-Audit-Tenant-Id"] = audit;
        const res = await fetch(`${BACKEND_URL}${row.photo_url}`, { headers });
        if (!res.ok) return;
        const b = await res.blob();
        createdUrl = URL.createObjectURL(b);
        if (!cancelled) setBlobUrl(createdUrl);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [row?.photo_url]);

  if (blobUrl) {
    return (
      <>
        <div className="flex items-center gap-2">
          <img src={blobUrl} alt="" onClick={(e) => { e.stopPropagation(); setOpen(true); }} style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: "2px solid var(--accent)" }} />
          <span>{row.name}</span>
        </div>
        {open && (
          <Modal open onClose={() => setOpen(false)} title={row.name} size="md">
            <img src={blobUrl} alt="" style={{ maxWidth: "100%", maxHeight: "70vh", display: "block", margin: "0 auto" }} />
          </Modal>
        )}
      </>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--accent)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700 }}>{initials}</div>
      <span>{row?.name}</span>
    </div>
  );
}

export default function Brigades() {
  const [tab, setTab] = useState("brigades");
  return (
    <div data-testid="brigades-module">
      <div className="flex items-center justify-between mb-3">
        <h1 className="card-h1" style={{ margin: 0 }}>Respuesta a Emergencias Ambientales</h1>
        <PDFButton endpoint="/pdf/module/racs" filename="reporte_respuesta_emergencias.pdf" label="Reporte PDF" />
      </div>
      <div className="tabs-bar">
        {TABS.map((t) => <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)} data-testid={`br-tab-${t.key}`}>{t.label}</button>)}
      </div>
      {tab === "brigades" ? (
        <BrigadistasWithPhoto />
      ) : tab === "msds" ? (
        <HdsWithProviders />
      ) : tab === "spills" ? (
        <SpillsTab />
      ) : tab === "emergency_procedures" ? (
        <CrudTable collection="emergency_procedures" testIdPrefix="emergency-procedures" fields={fieldsByColl.emergency_procedures} />
      ) : (
        <CrudTable collection={tab} testIdPrefix={tab} fields={fieldsByColl[tab]} />
      )}
    </div>
  );
}

function SpillsTab() {
  return (
    <CrudTable
      collection="emergency_reports"
      filters={{ type: "Derrame" }}
      testIdPrefix="spills"
      fields={[
        { key: "code", label: "# Reporte" },
        { key: "type", label: "Tipo", default: "Derrame", hiddenInForm: true, hidden: true },
        { key: "area", label: "Área" },
        { key: "substance", label: "Sustancia derramada" },
        { key: "volume_l", label: "Volumen estimado (L)", type: "number" },
        { key: "reported_by", label: "Reporta" },
        { key: "attended_by", label: "Atiende" },
        { key: "date", label: "Fecha", type: "date" },
        { key: "notification_time", label: "Hora aviso (HH:MM)" },
        { key: "notification_channel", label: "Medio de aviso", type: "select", options: ["Radio", "Teléfono", "Correo", "Presencial", "Alarma", "Otro"] },
        { key: "containment_action", label: "Acción de contención", type: "textarea", full: true },
        { key: "status", label: "Status", type: "select", options: ["Abierto", "En atención", "Cerrado"] },
        { key: "lesson_learned", label: "Lección aprendida", type: "textarea", full: true },
      ]}
    />
  );
}

function BrigadistasWithPhoto() {
  const [uploading, setUploading] = useState(null); // row id while uploading
  const [tick, setTick] = useState(0);

  async function uploadPhoto(row, file) {
    if (!file) return;
    setUploading(row.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("module", "brigades");
      fd.append("parent_id", row.id);
      const token = localStorage.getItem("token");
      const audit = localStorage.getItem("audit_tenant_id");
      const headers = { Authorization: `Bearer ${token}` };
      if (audit) headers["X-Audit-Tenant-Id"] = audit;
      const res = await fetch(`${BACKEND_URL}/api/files/upload`, { method: "POST", headers, body: fd });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const url = `/api/files/view/${data.id}`;
      await api.patch(`/data/brigades/${row.id}`, { photo_url: url });
      setTick(tick + 1);
    } catch (e) {
      alert("Error subiendo foto: " + e.message);
    } finally { setUploading(null); }
  }

  // Wrap the standard CrudTable in a div with a photo upload extra-action.
  // The render of the name cell already shows the photo (BrigadistaCell).
  return (
    <CrudTable
      key={tick}
      collection="brigades"
      testIdPrefix="brigades"
      fields={fieldsByColl.brigades}
      extraActions={[
        {
          label: "Foto",
          icon: <User size={12} />,
          onClick: (row) => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/png,image/jpeg,image/jpg";
            input.onchange = (e) => uploadPhoto(row, e.target.files?.[0]);
            input.click();
          },
        },
      ]}
    />
  );
}

function HdsWithProviders() {
  const [providers, setProviders] = useState([]);
  useEffect(() => {
    api.get("/data/providers").then(({ data }) => setProviders(data || [])).catch(() => {});
  }, []);
  const fields = fieldsByColl.msds.map((f) => {
    if (f.key === "provider_id") {
      return { ...f, options: providers.map((p) => ({ value: p.id, label: p.name })) };
    }
    return f;
  });
  return <CrudTable collection="msds" testIdPrefix="msds" fields={fields} />;
}
